// Web Speech API wrapper. Browser-native, free, instant.

let cachedVoices = [];

function loadVoices() {
  return new Promise((resolve) => {
    const v = window.speechSynthesis?.getVoices?.() || [];
    if (v.length) {
      cachedVoices = v;
      resolve(v);
      return;
    }
    const handler = () => {
      cachedVoices = window.speechSynthesis.getVoices();
      window.speechSynthesis.removeEventListener("voiceschanged", handler);
      resolve(cachedVoices);
    };
    window.speechSynthesis?.addEventListener?.("voiceschanged", handler);
    // iOS Safari sometimes never fires 'voiceschanged' — poll a few times as fallback
    let tries = 0;
    const poll = setInterval(() => {
      const list = window.speechSynthesis?.getVoices?.() || [];
      tries += 1;
      if (list.length || tries > 8) {
        clearInterval(poll);
        cachedVoices = list;
        window.speechSynthesis?.removeEventListener?.("voiceschanged", handler);
        resolve(list);
      }
    }, 250);
  });
}

export async function getVoices() {
  if (cachedVoices.length) return cachedVoices;
  return loadVoices();
}

export function isSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function cancelSpeech() {
  if (isSupported()) window.speechSynthesis.cancel();
}

/**
 * Speak text. Returns a Promise that resolves when speaking ends.
 * options: { rate, pitch, voiceName, lang, onStart, onEnd }
 */
export async function speak(text, options = {}) {
  if (!isSupported() || !text) return;
  const { rate = 0.95, pitch = 1.05, voiceName, lang = "en-US", onStart, onEnd } = options;

  // Cancel any in-flight speech so taps feel instant
  window.speechSynthesis.cancel();

  const voices = await getVoices();
  const utt = new SpeechSynthesisUtterance(text);
  utt.rate = rate;
  utt.pitch = pitch;
  utt.lang = lang;

  const chosen =
    (voiceName && voices.find((v) => v.name === voiceName)) ||
    voices.find((v) => v.lang?.toLowerCase().startsWith(lang.toLowerCase())) ||
    voices.find((v) => v.default) ||
    voices[0];
  if (chosen) utt.voice = chosen;

  return new Promise((resolve) => {
    let didStart = false;
    utt.onstart = () => { didStart = true; onStart && onStart(); };
    utt.onend = () => {
      onEnd && onEnd();
      // didStart=true + onend fired = TTS played to completion. Used by
      // pilot instrumentation to measure "spoken_ok" reliability on real
      // devices (iOS Safari rate-limits silently in some contexts).
      resolve({ spoken_ok: didStart });
    };
    utt.onerror = () => {
      onEnd && onEnd();
      resolve({ spoken_ok: false });
    };
    window.speechSynthesis.speak(utt);
  });
}
